/**
 * @file card-shapes.ts
 * @description Вид карточек сайта числами (SB11; SB31; phase19.16, партия
 * SB120, этап 2 — uiux-sb120-stage2 §2 «Карточки и фото», §5; данные
 * db-design-sb120-stage2 §1 `shapes.radius {card, field}`, `shapes.cards
 * {border {width, color?}, shadow}`, каталог `card.look own {radius, cards}`).
 *
 * Одно место для «Оформление → Кнопки и карточки → Карточки и фото» и
 * «Каталог → Карточка товара → Вид карточки» (решение пользователя
 * 2026-10-12: поля одни, разный охват): быстрые схемы «Нет / Мягкое /
 * Крупное», стиль образца (те же функции CSS, что у витрины: `cardShadowCss`),
 * сводка «8/6 px · рамка 1 px · тень 0 %» и предупреждения «сливается с
 * фоном» / «рамка почти не видна» (не запрет, SB120).
 */

import {
  cardShadowCss,
  colorRefHex,
  contrastRatio,
  cssPx,
  deriveSiteColors,
  formatContrast,
  RADIUS_V1,
  requireHexColor,
  type SiteCards,
  type SiteColors,
} from "@chaiberi/shared";
import type { CSSProperties } from "react";

import { formatNumber } from "../site-fields/number-input";
import { opaqueBg } from "../site-fields/color-contrast";

/** Вид карточки для полей: скругление карточки, полей (только у темы), рамка и тень. */
export interface CardLookValue {
  radius: number;
  /** Скругление полей и бейджей; у карточки товара его нет. */
  field?: number;
  cards: SiteCards;
}

/** Быстрые схемы скругления: ставят оба числа сразу (прежние ступени v1). */
export const CARD_LOOK_SCHEMES: ReadonlyArray<{ id: string; label: string; card: number; field: number }> = [
  { id: "none", label: "Нет", card: RADIUS_V1.none.card, field: RADIUS_V1.none.field },
  { id: "soft", label: "Мягкое", card: RADIUS_V1.soft.card, field: RADIUS_V1.soft.field },
  { id: "large", label: "Крупное", card: RADIUS_V1.large.card, field: RADIUS_V1.large.field },
];

/** Порог заметности рамки (WCAG 1.4.11 «не текст»: 3:1). */
export const BORDER_CONTRAST_MIN = 3;

const px = (v: number) => formatNumber(v, 2);

/** «8/6 px · рамка 1 px · тень 0 %» (у карточки товара — «8 px · …»). */
export function cardLookText(value: CardLookValue): string {
  const radius = value.field === undefined ? `${px(value.radius)} px` : `${px(value.radius)}/${px(value.field)} px`;
  const border = value.cards.border.width > 0 ? `рамка ${value.cards.border.width} px` : "без рамки";
  return `${radius} · ${border} · тень ${px(value.cards.shadow)} %`;
}

/** Цвет рамки hex: свой или роль; нет ключа — линия сайта (`--site-line`). */
export function cardBorderHex(border: SiteCards["border"], colors: SiteColors): string {
  return border.color ? colorRefHex(border.color, colors) : deriveSiteColors(colors).line;
}

/** Стиль образца карточки — как у витрины (скругление px, тень по шкале %, рамка). */
export function cardSampleStyle(value: Pick<CardLookValue, "radius" | "cards">, colors: SiteColors): CSSProperties {
  const { border, shadow } = value.cards;
  return {
    background: colors.cardBg,
    color: colors.text,
    borderRadius: cssPx(value.radius),
    boxShadow: cardShadowCss(shadow),
    border: border.width > 0 ? `${border.width}px solid ${cardBorderHex(border, colors)}` : undefined,
  };
}

/** Контраст рамки с фоном страницы; `null` — цвет битый. */
function borderContrast(border: SiteCards["border"], colors: SiteColors): number | null {
  const flat = opaqueBg(cardBorderHex(border, colors), colors);
  if (!flat) return null;
  try {
    return contrastRatio(requireHexColor(flat), requireHexColor(colors.pageBg));
  } catch {
    return null;
  }
}

/** Цвет без регистра и без непрозрачной альфы: #FFFFFFFF → #ffffff (у #ffffff ничего не срезается). */
const normColor = (c: string) => c.toLowerCase().replace(/^(#[0-9a-f]{6})ff$/i, "$1");

/** Цвета совпадают без учёта регистра и непрозрачной альфы «ff». */
const sameColor = (a: string, b: string) => normColor(a) === normColor(b);

/**
 * Предупреждения вида карточек (§5, не запрет): без рамки и тени карточка
 * цвета фона сливается с ним; свой цвет рамки слабее 3:1 к фону страницы.
 * «Авто: линия» — решение платформы (тонкая линия), её не оцениваем.
 */
export function cardLookWarnings(cards: SiteCards, colors: SiteColors): string[] {
  const warnings: string[] = [];
  if (cards.border.width === 0 && cards.shadow === 0 && sameColor(colors.cardBg, colors.pageBg)) {
    warnings.push("Без рамки и тени карточка сливается с фоном: цвет карточки = фон страницы.");
  }
  if (cards.border.width > 0 && cards.border.color) {
    const ratio = borderContrast(cards.border, colors);
    if (ratio !== null && ratio < BORDER_CONTRAST_MIN) {
      warnings.push(`Рамка почти не видна: контраст с фоном ${formatContrast(ratio)}:1, заметна от ${BORDER_CONTRAST_MIN}:1.`);
    }
  }
  return warnings;
}
